import { FCC } from "@/types/common";
import { useForm, useWatch } from "react-hook-form";
import { Box, Stack } from "@mui/material";
import { Form } from "@/components/elements/Form";
import { yupResolver } from "@hookform/resolvers/yup";
import { MenuNavigate } from "@/components/elements/MenuNavigate";
import { Button } from "@/components/elements/Button";
import { JobTitle } from "@/features/job/components/JobTitle";
import { useGetJobSchema } from "@/features/job/hooks/useGetJobSchema";
import { TCompanyExist, TJobForm } from "@/features/job/types/JobForm";
import { JobStatus } from "@/features/job/components/job-form/JobStatus";
import { JobBasicInfo } from "@/features/job/components/job-form/JobBasicInfo";
import { JobEmploymentCondition } from "@/features/job/components/job-form/JobEmploymentCondition";
import { JobWorkingCondition } from "@/features/job/components/job-form/JobWorkingCondition";
import { JobRequirement } from "@/features/job/components/job-form/JobRequirement";
import { JobBenefit } from "@/features/job/components/job-form/JobBenefit";
import { JobFeature } from "@/features/job/components/job-form/JobFeature";
import { CreateRecruitmentSourceModal } from "@/features/job/components/job-form/CreateRecruitmentSourceModal";
import { useVisible } from "@/hooks/useVisible";
import { useGetRecruitmentSourceList } from "@/api/services/main/recruitment-source";
import { useGetUserListPersonInCharge } from "@/api/services/main/user";
import { JobUploadFiles } from "@/features/job/components/job-form/JobUploadFiles";
import { JOB_STATUS } from "@/types/enum";
import { cleanObject, convertJobFormDataReq } from "@/features/job/utils/convertJobFormData";
import { toast } from "@/utils/toast";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { AppRoutes } from "@/routes/config";
import { showError } from "@/utils/error";
import { createJobApi, updateJobApi } from "@/api/services/main/job";
import { useNavigate, useParams } from "react-router-dom";
import { useCallback, useEffect, useRef, useState } from "react";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { queryClient } from "@/constants/queryClient";
import { getDynamicRoute } from "@/utils/url";
import { IconSave } from "@/assets/icons";
import { CreateCompanyModal } from "@/features/job/components/job-form/CreateCompanyModal";
import { getCompanyExists, useCompanyList } from "@/api/services/main/company";
import { getFirstErrorPath } from "@/features/job/utils/getFirstErrorPath";
import { FooterContainer } from "@/components/commons/FooterContainer";
import { InputRuleBanner } from "@/components/elements/InputRuleBanner";
import { JOB_WIDTH_FULL } from "@/features/job/constants/width";
import { useGetMatchInfo } from "@/api/services/main/match";
import { isEqual } from "lodash";

type TJobFormProps = {
  defaultValues: Partial<TJobForm>;
};
export const JobForm: FCC<TJobFormProps> = ({ defaultValues }) => {
  const navigate = useNavigate();
  const { jobSchema } = useGetJobSchema();
  const { recruitmentSourceOptions, isLoading: isLoadingRecruitmentSource, refetch: refetchRecruitmentSources } = useGetRecruitmentSourceList();
  const {
    companyOptions,
    isLoading: isLoadingCompanyList,
    refetch: refetchCompanyList,
  } = useCompanyList({ getCompanyArchiveById: defaultValues?.companyId });
  const { userOptions, isLoading: isLoadingUserList } = useGetUserListPersonInCharge(defaultValues?.recruiterId);
  const createRecruitmentSourceModal = useVisible();
  const createCompanyModal = useVisible();
  const { jobId } = useParams();
  const { data: matchData } = useGetMatchInfo();
  const onboardingSupportRef = useRef<HTMLDivElement | null>(null);
  const selfExpenseRef = useRef<HTMLDivElement | null>(null);
  const customFieldsKeys = ["jobWorkingTimes", "selfExpense", "onboardingSupport", "relatedFileTitles"];
  const [enableBlocker, setEnableBlocker] = useState(false);
  const [redirect, setRedirect] = useState<{ route: string } | null>(null);
  const [companyExists, setCompanyExists] = useState<TCompanyExist[]>(defaultValues.companyExists ?? []);

  const methods = useForm<TJobForm>({
    defaultValues,
    resolver: yupResolver(jobSchema),
    mode: "all",
    reValidateMode: "onBlur",
  });

  const { control, handleSubmit, setValue, trigger } = methods;
  const watchValue = methods.watch();

  const status = useWatch({ control, name: "status" });
  const isDraft = status === JOB_STATUS.DRAFT;

  const prevStatusRef = useRef(status);
  useEffect(() => {
    if (prevStatusRef.current !== status) {
      prevStatusRef.current = status;
      const conditionalFields: (keyof TJobForm)[] = [
        "matchOfficeId",
        "companySpecificIndustryId",
        "businessClassificationId",
        "recruitmentNumber",
        "employmentType",
        "bonus",
        "salaryRaise",
        "workplace",
        "jobWorkingTimes",
        "jobSalarySummaries",
        "nationalityIds",
        "recruitmentLocationType",
        "residenceStatusIds",
      ];
      trigger(conditionalFields);
    }
  }, [status, trigger]);

  const companySpecificIndustryIdWatchField = useWatch({
    control,
    name: "companySpecificIndustryId",
  });

  useEffect(() => {
    if (enableBlocker && redirect) {
      navigate(redirect.route);
      setRedirect(null);
    }
  }, [enableBlocker, redirect, navigate]);

  useEffect(() => {
    const hasChanged = !isEqual(cleanObject(watchValue), cleanObject(defaultValues));
    setEnableBlocker(hasChanged);
  }, [watchValue]);

  // Navigation helpers
  const getBackRoute = () => {
    return jobId ? getDynamicRoute(AppRoutes.jobDetail, { jobId }) : AppRoutes.jobList;
  };

  // Back button handlers
  const handleBackClick = () => {
    navigate(getBackRoute());
  };

  // Handle newly created company auto-selection
  const handleCompanyCreated = (companyId: string) => {
    setValue("companyId", companyId, { shouldValidate: true });
  };

  // Check if company exists when companyId changes
  const handleCompanyChange = useCallback(
    async (companyName: string) => {
      if (!companyName) {
        setCompanyExists([]);
        return;
      }
      try {
        const { data } = await getCompanyExists("job", companyName);
        if (data && data.length > 0) {
          setCompanyExists(data);
        } else {
          setCompanyExists([]);
        }
      } catch {
        setCompanyExists([]);
      }
    },
    [jobId],
  );

  // Handle newly created recruitment source auto-selection
  const handleRecruitmentSourceCreated = (recruitmentSourceId: string) => {
    const currentIds = methods.watch("recruitmentSourceIds") || [];
    setValue("recruitmentSourceIds", [...currentIds, recruitmentSourceId], { shouldValidate: true });
  };

  const onSubmit = async (data: TJobForm) => {
    await handleSaveData(data);
  };

  const handleSaveData = async (data: TJobForm) => {
    try {
      data.jobDocumentFiles = data.jobDocumentFiles?.filter((file) => file instanceof File);
      const payload = convertJobFormDataReq(data);

      if (jobId) {
        await updateJobApi(jobId, payload);
        toast.success(API_SUCCESS_MESSAGE);

        await queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.JOB.FETCH_DETAIL, jobId],
        });
        const route = getDynamicRoute(AppRoutes.jobDetail, { jobId: jobId });
        setEnableBlocker(true);
        setRedirect({ route });
      } else {
        await createJobApi(payload);
        toast.success(API_SUCCESS_MESSAGE);
        navigate(AppRoutes.jobList);
        setEnableBlocker(true);
        setRedirect({ route: AppRoutes.jobList });
      }
    } catch (e) {
      showError(e);
    }
  };

  // Check the first validation error for custom fields that require manual scrolling
  useEffect(() => {
    const currentErrors = methods.formState.errors;
    const isValidNow = methods.formState.isValid;

    if (isValidNow && currentErrors) {
      const firstErrorKey = Object.keys(currentErrors)[0];
      if (customFieldsKeys.includes(firstErrorKey) && currentErrors?.selfExpense) {
        setTimeout(() => {
          selfExpenseRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }, 0);
      } else if (customFieldsKeys.includes(firstErrorKey) && currentErrors?.onboardingSupport) {
        setTimeout(() => {
          onboardingSupportRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }, 0);
      }
    }

    if (!isValidNow && currentErrors) {
      const firstErrorKey = Object.keys(currentErrors)[0];

      // Only scroll if the first error key is one of the custom fields
      if (firstErrorKey && customFieldsKeys.includes(firstErrorKey)) {
        if (currentErrors?.jobWorkingTimes) {
          const firstErrorPath = getFirstErrorPath(currentErrors);
          const formatPathToId = (path: string) => path.replace(/\[(\d+)\]/g, "-$1").replace(/\./g, "-");
          const fieldId = firstErrorPath ? formatPathToId(firstErrorPath) : null;
          if (fieldId) {
            setTimeout(() => {
              const errorFieldElement = document.getElementById(fieldId);
              if (errorFieldElement) {
                errorFieldElement.scrollIntoView({
                  behavior: "smooth",
                  block: "center",
                });
              }
            }, 0);
          }
        } else if (currentErrors?.onboardingSupport) {
          setTimeout(() => {
            onboardingSupportRef.current?.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }, 0);
        } else if (currentErrors?.relatedFileTitles) {
          const value = currentErrors.relatedFileTitles;
          let fieldId: string | null = null;
          if (Array.isArray(value)) {
            for (let i = 0; i < value.length; i++) {
              const item = value[i];
              if (item && typeof item === "object" && Object.keys(item).length > 0) {
                fieldId = `relatedFileTitles-${i}`;
                break;
              }
            }
          }
          if (fieldId) {
            setTimeout(() => {
              const errorFieldElement = document.getElementById(fieldId);
              if (errorFieldElement) {
                errorFieldElement.scrollIntoView({
                  behavior: "smooth",
                  block: "center",
                });
              }
            }, 0);
          }
        }
      }
    }
  }, [methods.formState.errors, methods.formState.isValid]);

  return (
    <>
      <JobTitle
        title={jobId ? defaultValues?.jobName : "新規求人作成"}
        tagIds={defaultValues?.tagIds}
        status={defaultValues?.status}
        createdAt={defaultValues?.createdAt}
        updatedAt={defaultValues?.updatedAt}
        isCompanyArchived={defaultValues?.company?.isArchive}
      />
      <Form methods={methods} onSubmit={onSubmit} schema={jobSchema} enableBlocker={enableBlocker}>
        <Box width={`${JOB_WIDTH_FULL}px`}>
          <InputRuleBanner rule={matchData?.data?.jobInputRule} />
          <MenuNavigate
            contents={[
              {
                title: "ステータス状況",
                content: (
                  <JobStatus
                    isDraft={isDraft}
                    originalStatus={defaultValues?.status}
                    userOptions={userOptions}
                    isLoadingUserList={isLoadingUserList}
                    recruiterId={defaultValues?.recruiterId}
                  />
                ),
              },
              {
                title: "基本情報",
                content: (
                  <JobBasicInfo
                    isDraft={isDraft}
                    isLoadingCompany={isLoadingCompanyList}
                    companyOptions={companyOptions}
                    openCreateCompanyModal={createCompanyModal.open}
                    isLoadingRecruitmentSource={isLoadingRecruitmentSource}
                    recruitmentSourceOptions={recruitmentSourceOptions}
                    openCreateRecruitmentSourceModal={createRecruitmentSourceModal.open}
                    companySpecificIndustryIdWatchField={companySpecificIndustryIdWatchField}
                    onCompanyChange={handleCompanyChange}
                  />
                ),
              },
              { title: "雇用条件", content: <JobEmploymentCondition isDraft={isDraft} /> },
              { title: "勤務条件", content: <JobWorkingCondition isDraft={isDraft} /> },
              {
                title: "応募要件",
                content: <JobRequirement isDraft={isDraft} companySpecificIndustryIdWatchField={companySpecificIndustryIdWatchField} />,
              },
              { title: "福利厚生", content: <JobBenefit isDraft={isDraft} /> },
              {
                title: "求人特徴",
                content: <JobFeature isDraft={isDraft} onboardingSupportRef={onboardingSupportRef} selfExpenseRef={selfExpenseRef} />,
              },
              { title: "その他関連情報", content: <JobUploadFiles isDraft={isDraft} /> },
            ]}
            contentWidth="900px"
            contentAffix={
              <FooterContainer>
                <Stack gap={1} direction="row" justifyContent="space-between" alignItems="center">
                  <Button variant="ghost" size="sm" onClick={handleBackClick}>
                    戻る
                  </Button>
                  <Stack direction="row" gap={3}>
                    <Button type="submit" variant="primary" startIcon={<IconSave />} size="sm" onClick={handleSubmit(onSubmit)}>
                      保存
                    </Button>
                  </Stack>
                </Stack>
              </FooterContainer>
            }
          />
        </Box>
      </Form>

      <CreateCompanyModal modal={createCompanyModal} refetchCompanyList={refetchCompanyList} onCompanyCreated={handleCompanyCreated} />
      <CreateRecruitmentSourceModal
        modal={createRecruitmentSourceModal}
        refetchRecruitmentSources={refetchRecruitmentSources}
        onRecruitmentSourceCreated={handleRecruitmentSourceCreated}
      />
    </>
  );
};
